import { create } from "apisauce";
import { toast } from "react-toastify";
import { BehaviorSubject } from "rxjs";
import {
  apiBaseUrl,
  apiBaseUrlForay,
  apiBaseUrlUsers,
} from "../constants/environment.constants";
import { QUERY_PARAMS_KEY, STORAGE_KEYS } from "../constants/keys";
import { removeUser } from "../features/Auth Slice/authSlice";
import { store } from "../store/store";
import {
  IAuthReqBody,
  ICenterRegistrationReqBody,
  ICustomSignUpReqBody,
  INewPassword,
  IProfileCompleteReqBody,
  IReqToResetPassword,
  ISocialAuthReqBody,
  ISocialSignUpReqBody,
} from "../types/auth.interface";
import { readDeviceTrustToken, storeDeviceTrustToken } from "../utils/deviceTrust";
import { getMessage } from "../utils/labelsMessage";
import spinnerSvc from "../utils/spinner-service";
import { storeTokens, storeUser } from "../utils/storingInStorage";
import { api } from "../utils/axios";

const isAuthenticatedSub = new BehaviorSubject(false);
// const signedIn$ = new Subject();

const DEACTIVATION_ERRORS = new Set([
  "user_deactivated",
  "center_deactivated",
  "parent_deactivated",
]);

export const authApi = create({
  baseURL: apiBaseUrl,
  headers: { Accept: "application/json" },
});

authApi.axiosInstance.interceptors.request.use(
  async (config) => {
    spinnerSvc.start();
    return config;
  },
  (err) => {
    spinnerSvc.stop();
    console.error(err);
    return Promise.reject(err);
  },
);

authApi.axiosInstance.interceptors.response.use(
  (response) => {
    spinnerSvc.stop();
    return response;
  },
  (err) => {
    spinnerSvc.stop();
    if (err?.response?.data?.prevUser) {
      //openModal
      localStorage.setItem(
        STORAGE_KEYS.PREV_USER,
        JSON.stringify(err?.response?.data?.prevUser),
      );
    }
    if (err?.response?.status === 413) {
      // Check for the uploaded file in the request (if available)
      const fileName = err?.config?.data?.get("files")?.name; // Assuming the file was uploaded via FormData with "file" key
      const message = `The file ${
        fileName || "you are uploading"
      } is too large to upload.`;

      toast.error(message); // Show toast error with the message
      return Promise.reject(err);
    }
    if (err?.response?.data?.error === "is_already_verified") {
      // const urlParams = new URLSearchParams(window.location.search);
      // const redirectURI = urlParams.get("redirect_uri");
      // const clientID = urlParams.get(QUERY_PARAMS_KEY.CLIENT_ID);
      // window.location.replace(`/sign-in${clientID ? "?client_id=" + clientID : ""}${redirectURI ? "&redirect_URI=" + redirectURI : ""
      //   }`)
      return Promise.reject(err);
    }
    // These open DeactivationModal in LoginForm instead; they have no label
    // entry, so the generic toast was an empty pop-up on top of it.
    if (DEACTIVATION_ERRORS.has(err?.response?.data?.error)) {
      return Promise.reject(err);
    }
    const message = getMessage(err?.response?.data?.error);
    toast.error(message); /// had to check the status of the response and based on that set toast type
    if (err?.response?.data?.error === "user_not_verified") {
      return err?.response;
    }
    if (err?.response?.status === 409 && err?.response?.data?.accounts) {
      return err?.response;
    }
    return Promise.reject(err);
  },
);

export const usersApi = create({
  baseURL: apiBaseUrlUsers,
  headers: { Accept: "application/json" },
});

usersApi.axiosInstance.interceptors.request.use(
  async (config: any) => {
    const accessToken = localStorage.getItem(STORAGE_KEYS?.ACC_TOKEN);
    if (accessToken !== "") {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    spinnerSvc.start();
    return config;
  },
  (err) => console.error(err),
);

usersApi.axiosInstance.interceptors.response.use(
  (response) => {
    spinnerSvc.stop();
    return response;
  },

  async (err) => {
    spinnerSvc.stop();
  },
);

export const forayApi = create({
  baseURL: apiBaseUrlForay,
  headers: { Accept: "application/json" },
});

forayApi.axiosInstance.interceptors.request.use(
  async (config: any) => {
    const accessToken = localStorage.getItem(STORAGE_KEYS?.ACC_TOKEN);
    if (accessToken !== "") {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    spinnerSvc.start();
    return config;
  },
  (err) => console.error(err),
);

forayApi.axiosInstance.interceptors.response.use(
  (response) => {
    spinnerSvc.stop();
    return response;
  },
  async (err) => {
    spinnerSvc.stop();
  },
);

export const signIn = async (
  redirectUri: string,
  clientID: string,
  reqBody?: IAuthReqBody | ISocialAuthReqBody,
  rememberMe?: boolean,
  rfToken?: string,
  account_type?: string,
) => {
  try {
    const requestConfig: Record<string, any> = {};
    if (rfToken) requestConfig.headers = { Authorization: `Bearer ${rfToken}` };
    if (account_type) requestConfig.params = { account_type };

    const deviceTrustToken = readDeviceTrustToken();
    const body = deviceTrustToken ? { ...reqBody, deviceTrustToken } : reqBody;

    const res: any = await authApi.post(
      `/authorize?client_id=${clientID}&redirect_uri=${redirectUri}`,
      body,
      Object.keys(requestConfig).length > 0 ? requestConfig : undefined,
    );
    if (res?.ok && res?.data?.mfaRequired) {
      // Password/social credentials were valid, but the account has MFA
      // turned on — no tokens to store yet, a code challenge must be
      // completed first (see verifyMfaLoginCode).
      return { mfaRequired: true, data: res?.data, ok: true, isVerified: true };
    } else if (res?.ok && res?.data?.mfaEnrollmentRequired) {
      // Credentials were valid, but this account has used up its free
      // logins without turning MFA on — it must complete an enrollment
      // code challenge (no password step, unlike mfaRequired's sibling
      // flows — see confirmMfaEnrollment) before a session is granted.
      return {
        mfaEnrollmentRequired: true,
        data: res?.data,
        ok: true,
        isVerified: true,
      };
    } else if (res?.ok) {
      storeTokens(res?.data, rememberMe);
      storeUser(res?.data?.access_token);
      return { data: res?.data, ok: true, isVerified: true };
    } else if (res?.data?.error === "user_not_verified") {
      return {
        error: true,
        isVerified: false,
      };
    } else {
      if (res?.status === 409 && res?.data?.accounts) {
        return {
          error: true,
          status: 409,
          data: res?.data,
          isVerified: true,
        };
      }
      return {
        error: res?.data?.error,
        token: res?.originalError?.response?.data?.access_Token,
        isVerified: true,
        // LoginForm reads data.error to open DeactivationModal
        // (user_/center_/parent_deactivated) — it was never passed through.
        status: res?.status,
        data: res?.data,
      };
    }
  } catch (err: any) {
    // Handle 409 Multiple Accounts Found error
    if (err?.response?.status === 409 && err?.response?.data?.accounts) {
      return {
        error: true,
        status: 409,
        data: err?.response?.data,
        isVerified: true,
      };
    }
    return {
      error: true,
      token: err?.response?.data?.access_Token,
      isVerified: true,
      status: err?.response?.status,
      data: err?.response?.data,
    };
    // toast(err?.response?.date?.error);
  }
};

// Sends the code for a challenge returned by signIn() (mfaRequired or
// mfaEnrollmentRequired) via the chosen channel — a separate step from
// creating the challenge so the channel can be picked fresh each time
// rather than being decided for the user. Must succeed before
// verifyMfaLoginCode/confirmMfaEnrollment or resendMfaLoginCode can do
// anything with this challengeId.
export const startMfaChallenge = async (
  challengeId: string,
  channel: "email" | "sms",
) => {
  try {
    const res: any = await authApi.post(`/login/mfa/start`, {
      challengeId,
      channel,
    });
    if (res?.ok) {
      return {
        ok: true,
        channel: res?.data?.channel as "email" | "sms",
        expiresInSeconds: res?.data?.expiresInSeconds as number,
        resendCooldownSeconds: res?.data?.resendCooldownSeconds as number,
      };
    }
    return { error: true, errorCode: res?.data?.error as string | undefined };
  } catch (err: any) {
    return {
      error: true,
      errorCode: err?.response?.data?.error as string | undefined,
    };
  }
};

// Shared by verifyMfaLoginCode/confirmMfaEnrollment below — both endpoints
// return the exact same success shape (tokens to store, optional
// deviceTrustToken) once their respective code is confirmed; only the URL
// and what happens server-side before that response differs.
const completeMfaCodeChallenge = async (
  url: string,
  challengeId: string,
  code: string,
  rememberMe?: boolean,
  trustDevice?: boolean,
) => {
  try {
    const res: any = await authApi.post(url, {
      challengeId,
      code,
      trustDevice,
    });
    if (res?.ok) {
      storeTokens(res?.data, rememberMe);
      storeUser(res?.data?.access_token);
      if (res?.data?.deviceTrustToken) {
        storeDeviceTrustToken(res.data.deviceTrustToken);
      }
      return { data: res?.data, ok: true, isVerified: true };
    }
    return {
      error: true,
      errorCode: res?.data?.error as string | undefined,
      attemptsRemaining: res?.data?.attemptsRemaining as number | undefined,
    };
  } catch (err: any) {
    return {
      error: true,
      errorCode: err?.response?.data?.error as string | undefined,
      attemptsRemaining: err?.response?.data?.attemptsRemaining as
        | number
        | undefined,
    };
  }
};

// Completes a login started by signIn() once mfaRequired came back — same
// success shape as signIn()'s non-MFA path (tokens stored, { data, ok,
// isVerified: true }) so callers can finish the login identically either
// way. On success with trustDevice, also persists the returned
// deviceTrustToken so a future signIn() from this device skips the code.
export const verifyMfaLoginCode = async (
  challengeId: string,
  code: string,
  rememberMe?: boolean,
  trustDevice?: boolean,
) =>
  completeMfaCodeChallenge(
    "/login/mfa/verify",
    challengeId,
    code,
    rememberMe,
    trustDevice,
  );

// Completes a login started by signIn() once mfaEnrollmentRequired came
// back — confirming the code both turns MFA on for the account and
// completes the login in the same response, so the caller treats this
// identically to verifyMfaLoginCode's success path.
export const confirmMfaEnrollment = async (
  challengeId: string,
  code: string,
  rememberMe?: boolean,
  trustDevice?: boolean,
) =>
  completeMfaCodeChallenge(
    "/login/mfa/enroll/confirm",
    challengeId,
    code,
    rememberMe,
    trustDevice,
  );

export const resendMfaLoginCode = async (challengeId: string) => {
  try {
    const res: any = await authApi.post(`/login/mfa/resend`, { challengeId });
    if (res?.ok) {
      return {
        ok: true,
        channel: res?.data?.channel as "email" | "sms" | undefined,
        resendsRemaining: res?.data?.resendsRemaining as number,
      };
    }
    return {
      error: true,
      errorCode: res?.data?.error as string | undefined,
      retryAfterSeconds: res?.data?.retryAfterSeconds as number | undefined,
    };
  } catch (err: any) {
    return {
      error: true,
      errorCode: err?.response?.data?.error as string | undefined,
      retryAfterSeconds: err?.response?.data?.retryAfterSeconds as
        | number
        | undefined,
    };
  }
};

export const signUp = async (
  redirectUri: string,
  clientID: string,
  reqBody: ICustomSignUpReqBody | ISocialSignUpReqBody,
  rememberMe?: boolean,
) => {
  try {
    const res: any = await authApi.post(
      `/signup?client_id=${clientID}&redirect_uri=${redirectUri}`,
      reqBody,
      {
        headers: { Accept: "application/json" },
      },
    );

    if (res?.ok) {
      storeTokens(res?.data, rememberMe);
      storeUser(res?.data?.access_token);
      return { data: res?.data, ok: true };
    } else return { error: true, ok: false };
  } catch (err: any) {
    console.log("error: ", err);
  }
};

export const completeProfile = async (
  redirectUri: string,
  clientID: string,
  reqBody: IProfileCompleteReqBody,
) => {
  const accToken = localStorage.getItem(STORAGE_KEYS.ACC_TOKEN);
  try {
    const res: any = await authApi.put(
      `/updateProfile?client_id=${clientID}&redirect_uri=${redirectUri}`,
      reqBody,
      {
        headers: { Authorization: `Bearer ${accToken}` },
      },
    );
    if (res?.ok) {
      storeUser(res?.data?.access_token);
      return { data: res?.data, ok: true };
    } else return { error: true, ok: false };
  } catch (err: any) {
    console.log("error: ", err);
  }
};

export const centerRegistration = async (
  reqBody: ICenterRegistrationReqBody,
) => {
  const accToken = localStorage.getItem(STORAGE_KEYS.ACC_TOKEN);
  try {
    const res: any = await authApi.post(`/center`, reqBody, {
      headers: { Authorization: `Bearer ${accToken}` },
    });
    if (res?.ok) {
      storeUser(res?.data?.access_token);
      return { data: res?.data, ok: true };
    } else return { error: true, ok: false };
  } catch (err: any) {
    console.log("error: ", err);
  }
};

export const centerUpdate = async (reqBody: ICenterRegistrationReqBody) => {
  const accToken = localStorage.getItem(STORAGE_KEYS.ACC_TOKEN);
  try {
    const res: any = await authApi.put(`/center`, reqBody, {
      headers: { Authorization: `Bearer ${accToken}` },
    });
    if (res?.ok) {
      storeUser(res?.data?.access_token);
      return { data: res?.data, ok: true };
    } else return { error: true, ok: false };
  } catch (err: any) {
    console.log("error: ", err);
  }
};

export const refreshToken = async () => {
  try {
    const rfToken = localStorage.getItem(STORAGE_KEYS.RF_TOKEN);
    const rememberMe = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) as string,
    );
    const res: any = await authApi.post(`/refresh`, undefined, {
      headers: { Authorization: `Bearer ${rfToken}` },
    });
    if (res?.ok) {
      storeTokens(res?.data, rememberMe);
      return res?.data?.access_token;
    }
    throw new Error("expired token");
  } catch (err: any) {
    const message = getMessage(err?.response?.data?.error);
    toast(message);
    console.log("error: ", err);
  }
};

export const getTokens = async (clientId?: string, code?: string) => {
  try {
    await authApi.post(
      `/token?${clientId && "client_id=" + clientId}${
        clientId && code ? "&" : ""
      }${code && "code=" + code}`,
    );
  } catch (err: any) {
    console.log("error: ", err);
  }
};

//use authApi in forget password instead of "api" service
export const reqToResetPassword = async (params: IReqToResetPassword) => {
  try {
    let url = `/sendResetLink?email=${params?.email}&client_id=${params?.clientID}&redirect_uri=${params?.redirectUri}`;
    if (params?.userType) url += `&user_type=${params.userType}`;
    if (params?.centerId) url += `&centerId=${params.centerId}`;
    const response: any = await authApi.post(url);
    if (response?.ok) {
      return { mailSent: true };
    }
    if (response?.status === 409 && response?.data?.accounts) {
      return { mailSent: false, accounts: response.data.accounts };
    }
    return { mailSent: false };
  } catch (err: any) {
    console.log("error: ", err);
    return { mailSent: false };
  }
};

export const createNewPassword = async (params: INewPassword) => {
  try {
    let url = `/resetPassword?token=${params?.token}`;
    if (params?.userType) url += `&user_type=${params.userType}`;
    const response = await authApi.post(url, { newPassword: params?.newPassword });
    if (response && response?.ok) {
      return { ok: true };
    } else {
      return { ok: false };
    }
  } catch (err: any) {
    console.log("error: ", err);
    return { ok: false, status: err?.response?.status as number | undefined };
  }
};

export const fetchLabels = async () => {
  try {
    const response = await authApi.get("/labels");
    if (response?.ok) {
      return response?.data;
    }
  } catch (err) {
    console.log("error: ", err);
  }
};

export const verifyEmail = async (token: string) => {
  try {
    const response = await authApi.get(`/confirmation?token=${token}`);
    if (response?.ok) {
      return { data: response?.data, ok: true };
    }
  } catch (err) {
    console.log("error xhexk: ", err);
  }
};

export const verifyProvisionedUser = async (token: string) => {
  try {
    const res: any = await authApi.get(`/verify?token=${token}`);
    if (res?.ok) {
      storeTokens(res?.data);
      storeUser(res?.data?.access_token);
      return { data: res?.data, ok: true };
    }
    return { error: true, ok: false };
  } catch (err: any) {
    return { error: true, ok: false, errorCode: err?.response?.data?.error as string };
  }
};

export const confirmEmailChange = async (token: string) => {
  try {
    const response = await authApi.post(`/user/change-email/confirm`, { token });
    if (response?.ok) {
      return { data: response?.data, ok: true };
    }
    const data: any = response?.data;
    return {
      ok: false,
      status: response?.status,
      error: data?.error || "Unable to confirm email change",
    };
  } catch (err: any) {
    return {
      ok: false,
      status: err?.response?.status,
      error: err?.response?.data?.error || "Unable to confirm email change",
    };
  }
};

export const confirmStaffParentEmailChange = async (token: string) => {
  try {
    const response = await authApi.post(`/staff-parent/change-email/confirm`, { token });
    if (response?.ok) {
      return { data: response?.data, ok: true };
    }
    const data: any = response?.data;
    return {
      ok: false,
      status: response?.status,
      error: data?.error || "Unable to confirm email change",
    };
  } catch (err: any) {
    return {
      ok: false,
      status: err?.response?.status,
      error: err?.response?.data?.error || "Unable to confirm email change",
    };
  }
};

export const resendVerifyLink = async (
  clientId: string,
  redirectUri: string,
  email: string,
) => {
  try {
    const response = await api.post(
      `/resendLink?client_id=${clientId}&redirect_uri=${redirectUri}&email=${email}`,
    );
    if (response?.ok) {
      return { data: response?.data, ok: true };
    }
  } catch (err) {
    console.log("error: ", err);
  }
};

export const signOut = () => {
  //   notificationSocket?.disconnectSocket();
  store.dispatch(removeUser());
};

export const postFiles = async (formData: FormData) => {
  const accToken = localStorage.getItem(STORAGE_KEYS.ACC_TOKEN);
  try {
    const response = await usersApi.post(`/users/upload`, formData, {
      headers: { Authorization: `Bearer ${accToken}` },
    });
    if (response && response?.ok) {
      return { data: response?.data, ok: true };
    } else {
      throw new Error("Couldn't upload file");
    }
  } catch (err: any) {
    console.log("error: ", err);
  }
};

export const fetchAccountInfo = async () => {
  try {
    const res: any = await usersApi.get(`/users/user`);
    if (res?.ok) {
      // localStorage.setItem(STORAGE_KEYS.ROLE, res?.data?.roles);
      return { data: res?.data, ok: true };
    } else return { error: true, ok: false };
  } catch (err) {
    console.log("error: ", err);
  }
};

export const fetchStaffInfo = async () => {
  try {
    const res: any = await forayApi.get(`/staff-setup/my-info`);
    if (res?.ok) {
      return { data: res?.data?.staff, ok: true };
    } else return { error: true, ok: false };
  } catch (err) {
    console.log("error: ", err);
  }
};

export const fetchParentInfo = async () => {
  try {
    const res: any = await forayApi.get(`/parent-attendance/my-info`);
    if (res?.ok) {
      return { data: res?.data?.parent, ok: true };
    } else return { error: true, ok: false };
  } catch (err) {
    console.log("error: ", err);
  }
};

export const isAuthenticated = () => {
  return isAuthenticatedSub.asObservable();
};

export const setIsAuthenticated = (isAuthenticated: boolean) => {
  isAuthenticatedSub.next(isAuthenticated);
};
